/**
 * TypeScript types for Modern Order Details page
 * Matches OrderDetailsDTO specification from backend
 */

export type OrderStatus =
  | "CREATED"
  | "IN_PROGRESS"
  | "PACKED"
  | "READY"
  | "COMPLETED"
  | "ON_HOLD"
  | "CANCELLED";

export type Priority = "LOW" | "MEDIUM" | "HIGH";

export type PhotoTag = "FRONT" | "BACK" | "SIDE" | "OTHER";

export interface CustomerInfo {
  id: string;
  name: string;
  phone?: string;
}

export interface AssignedTailor {
  id: string;
  name: string;
}

export interface Price {
  materials?: number;
  labor?: number;
  discount?: number;
  total?: number;
  currency: string;
}

export interface OrderItem {
  id: string;
  garmentType: string; // "Kurta", "Pant", etc.
  fabric?: string; // "Cotton", "Silk"
  clothType?: string;
  quantity: number;
  unitPrice?: number;
  measurementSetId?: string;
}

export interface MeasurementField {
  key: string;
  label: string;
  value: string | number;
}

export interface MeasurementSet {
  id: string;
  name: string;
  updatedAt: string;
  fieldsPreview: MeasurementField[];
}

export interface OrderPhoto {
  id: string;
  url: string;
  thumbnailUrl?: string;
  tag?: PhotoTag;
  uploadedAt: string;
  uploadedBy: string;
}

export interface TimelineEntry {
  id: string;
  status: OrderStatus | "PENDING" | "IN_PROGRESS" | "COMPLETED" | "READY";
  title: string;
  detail?: string;
  at: string | null;
  by?: string | null;
  icon?: string;
  stage?: number;
  stageName?: string;
  // Enhanced fields
  assignedWorker?: {
    id: string;
    name: string;
  };
  estimatedStartAt?: string;
  estimatedCompletionAt?: string;
  actualStartedAt?: string;
  actualCompletedAt?: string;
  duration?: {
    hours: number;
    formatted: string;
    inProgress?: boolean;
  };
  notes?: string;
  internalNotes?: string; // Only visible to tailors/admins
}

export interface TimelineProgress {
  percentage: number;
  completedStages: number;
  totalStages: number;
  currentStage?: string | null;
}

export interface OrderNotes {
  customerNote?: string;
  internalNote?: string;
}

export interface Logistics {
  method?: "PICKUP" | "DELIVERY";
  address?: string;
  trackingCode?: string;
}

export interface AuditEntry {
  at: string;
  by: string;
  action: string;
  payload?: Record<string, unknown>;
}

/**
 * Receipt interface for payment receipts
 */
export interface Receipt {
  id: string;
  receiptNumber: string;
  orderId: string;
  paymentId?: string;
  invoiceId?: string;
  amount: number;
  remainingBalance: number;
  paymentMethod: string;
  transactionId?: string;
  paymentDate: string;
  receiptDate: string;
  gstAmount?: number;
  gstPercentage?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  notes?: string;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Receipt creation request
 */
export interface CreateReceiptRequest {
  payment_id?: string; // Optional - for linking to existing payment
  amount: number;
  payment_method: string;
  transaction_id?: string;
  payment_date?: string; // ISO 8601
  gst_percentage?: number;
  is_interstate?: boolean;
  notes?: string;
}

/**
 * OrderDetailsDTO - Complete order details response from backend
 */
export interface OrderDetailsDTO {
  id: string; // Order number like "ORD20251103152303"
  createdAt: string; // ISO 8601
  dueAt?: string; // ISO 8601
  status: OrderStatus;
  priority: Priority;
  customer: CustomerInfo;
  assignedTailor?: AssignedTailor;
  price?: Price;
  items: OrderItem[];
  measurementSet?: MeasurementSet;
  photos: OrderPhoto[];
  timeline: TimelineEntry[];
  notes?: OrderNotes;
  logistics?: Logistics;
  audit: AuditEntry[];
  receipts?: Receipt[]; // Receipts for this order
}

/**
 * API Response wrapper
 */
export interface OrderDetailsResponse {
  success?: boolean;
  data?: OrderDetailsDTO;
  error?: string;
  allowedNextStatuses?: string[];
  currentStatus?: string;
  requestedStatus?: string;
}

/**
 * Status transition request
 */
export interface StatusUpdateRequest {
  status: OrderStatus;
  note?: string;
  notifyChannels?: ("SMS" | "EMAIL" | "WHATSAPP")[];
}

/**
 * Photo upload request
 */
export interface PhotoUploadRequest {
  file: File;
  tag?: PhotoTag;
}

/**
 * Measurement set attachment request
 */
export interface MeasurementSetAttachmentRequest {
  measurementSetId: string;
}

/**
 * Partial order update request
 */
export interface OrderPatchRequest {
  priority?: Priority;
  dueAt?: string; // ISO 8601
  assignedTailorId?: string;
  price?: Price;
}

/**
 * Progress step definition for UI
 */
export interface ProgressStep {
  id: OrderStatus;
  label: string;
  hint: string;
}

/**
 * Status color mapping for UI
 */
export const STATUS_COLORS: Record<OrderStatus, string> = {
  CREATED: "gray",
  IN_PROGRESS: "blue",
  PACKED: "purple",
  READY: "teal",
  COMPLETED: "green",
  ON_HOLD: "amber",
  CANCELLED: "red",
};

/**
 * Priority color mapping for UI
 */
export const PRIORITY_COLORS: Record<Priority, string> = {
  LOW: "gray",
  MEDIUM: "amber",
  HIGH: "red",
};

/**
 * Progress steps for order status progression
 */
export const PROGRESS_STEPS: ProgressStep[] = [
  { id: "CREATED", label: "Order Created", hint: "We received your order" },
  { id: "IN_PROGRESS", label: "In Tailoring", hint: "Cutting, stitching, fittings" },
  { id: "PACKED", label: "Packed", hint: "Ready for dispatch" },
  { id: "READY", label: "Ready for pickup/delivery", hint: "Awaiting handover" },
  { id: "COMPLETED", label: "Complete", hint: "Delivered or picked up" },
];

/**
 * Get current progress step index
 */
export function getProgressStepIndex(status: OrderStatus): number {
  return PROGRESS_STEPS.findIndex((step) => step.id === status);
}

/**
 * Check if status is terminal (COMPLETED or CANCELLED)
 */
export function isTerminalStatus(status: OrderStatus): boolean {
  return status === "COMPLETED" || status === "CANCELLED";
}

